<script setup>
import { useCampaignAvailability } from 'dashboard/composables/useCampaignAvailability';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';
import CampaignProgress from './CampaignProgress.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  totalContacts: {
    type: Number,
    default: 0,
  },
  processedContacts: {
    type: Number,
    default: 0,
  },
  failedContacts: {
    type: Number,
    default: 0,
  },
  deliveredContacts: {
    type: Number,
    default: 0,
  },
  readContacts: {
    type: Number,
    default: 0,
  },
  repliedContacts: {
    type: Number,
    default: 0,
  },
  showEdit: { type: Boolean, default: false },
  showAnalytics: { type: Boolean, default: false },
  canEdit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'viewDetails',
  'analytics',
  'viewErrors',
  'pause',
  'resume',
]);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';
const STATUS_PAUSED = 'paused';

const { formatMessage } = useMessageFormatter();

const isProcessing = computed(() => props.status === STATUS_PROCESSING);
const isCompleted = computed(() => props.status === STATUS_COMPLETED);
const isPaused = computed(() => props.status === STATUS_PAUSED);

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value && !isProcessing.value && !isPaused.value,
  'text-n-brand': isProcessing.value,
  'text-n-amber-11': isPaused.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (isPaused.value) return t('CAMPAIGN.WHATSAPP.CARD.STATUS.PAUSED');
  if (isProcessing.value) return t('CAMPAIGN.WHATSAPP.CARD.STATUS.PROCESSING');

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});

const showProgress = computed(() => {
  return (
    !props.isLiveChatType &&
    (isProcessing.value || isCompleted.value || isPaused.value) &&
    props.totalContacts > 0
  );
});

const showPauseButton = computed(() => isProcessing.value);
const showResumeButton = computed(() => isPaused.value);

const showEditButton = computed(() => {
  if (props.isLiveChatType) return true;
  return (props.canEdit || props.showEdit) && props.status === 'active';
});
const { campaignsEnabled } = useCampaignAvailability();
</script>

<template>
  <CardLayout class="cursor-pointer" @click="emit('viewDetails')">
    <div class="flex flex-col gap-3 min-w-0">
      <!-- Header: Title + Status -->
      <div class="flex items-start justify-between gap-2 min-w-0">
        <h3
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-2 min-w-0 flex-1"
        >
          {{ title }}
        </h3>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2 shrink-0 whitespace-nowrap"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>

      <!-- Message Preview -->
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-2 [&>p]:mb-0"
      />

      <!-- Campaign Details / Progress -->
      <div class="flex items-center w-full gap-2">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <CampaignProgress
          v-else-if="showProgress"
          :total-contacts="totalContacts"
          :processed-contacts="processedContacts"
          :failed-contacts="failedContacts"
          :delivered-contacts="deliveredContacts"
          :read-contacts="readContacts"
          :replied-contacts="repliedContacts"
          :is-processing="isProcessing"
          @view-errors="emit('viewErrors')"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>

      <!-- Actions -->
      <div
        class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak"
      >
        <Button
          v-if="showAnalytics"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-lucide-chart-no-axes-column"
          :title="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
          @click.stop="emit('analytics')"
        />
        <Button
          v-if="showPauseButton"
          variant="faded"
          size="sm"
          color="amber"
          icon="i-lucide-pause"
          :label="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.PAUSE')"
          @click.stop="emit('pause')"
        />
        <Button
          v-if="showResumeButton"
          variant="faded"
          size="sm"
          color="blue"
          icon="i-lucide-play"
          :label="t('CAMPAIGN.WHATSAPP.CARD.ACTIONS.RESUME')"
          :disabled="!campaignsEnabled"
          @click.stop="emit('resume')"
        />
        <Button
          v-if="showEditButton"
          variant="faded"
          size="sm"
          color="slate"
          :icon="
            isLiveChatType ? 'i-lucide-sliders-vertical' : 'i-lucide-pencil'
          "
          :disabled="!campaignsEnabled"
          @click.stop="emit('edit')"
        />
        <Button
          variant="faded"
          color="ruby"
          size="sm"
          icon="i-lucide-trash"
          @click.stop="emit('delete')"
        />
      </div>
    </div>
  </CardLayout>
</template>
